import { useState, useEffect, useMemo } from "react";
import "../../styles/CustomSelect.scss";
import { SelectArrowIcon } from "../../svgIcons";
import { useLabelsContext } from "../../context/LabelsContext";
import { useConfigContext } from "../../context/ConfigContext";
import { RegistrationFields } from "../../models/registration-fields";

type DateSelectProps = {
	field: keyof RegistrationFields;
	onChange: (dateString: string) => void;
	value?: string;
	onBlur: () => void;
	hasError: boolean;
	minAge?: number;
};

type DateState = {
	day: number;
	month: number;
	year: number;
};

export const DateSelect: React.FC<DateSelectProps> = ({
	field,
	value,
	onBlur,
	onChange,
	hasError,
	minAge = 0,
}) => {
	const { config } = useConfigContext();
	const { labels } = useLabelsContext();

	const parseValue = (val?: string): DateState => {
		if (!val) return { day: 0, month: 0, year: 0 };

		const [year, month, day] = val.split("-").map(Number);

		return {
			year: year || 0,
			month: month || 0,
			day: day || 0,
		};
	};

	const [date, setDate] = useState<DateState>(() => parseValue(value));

	useEffect(() => {
		setDate(parseValue(value));
	}, [value]);

	const MONTH_NAMES = useMemo(
		() =>
			Array.from({ length: 12 }, (_, i) =>
				new Date(2000, i, 1).toLocaleString(config?.language, {
					month: "long",
				}),
			),
		[config?.language],
	);

	const yearOptions = useMemo(() => {
		const currentYear = new Date().getFullYear();
		const startYear = currentYear - 100;
		const maxYear = currentYear - minAge;

		return Array.from({ length: 101 }, (_, i) => startYear + i)
			.filter(year => year <= maxYear)
			.reverse();
	}, [minAge]);

	const emitChange = (next: DateState) => {
		const pad = (n: number) => String(n).padStart(2, "0");

		onChange(`${next.year}-${pad(next.month)}-${pad(next.day)}`);
	};

	return (
		<div id={field} className='club-form-selects'>
			{/* Day */}
			<div>
				{hasError && (
					<span className='error-message key-RequiredField'>
						{!date.day ? labels?.["RequiredField"] : <>&nbsp;</>}
					</span>
				)}
				<div className='club-form-select'>
					<select
						id='day'
						className={`form-field ${
							!date.day && hasError ? "has-error" : ""
						}`}
						value={date.day || ""}
						onChange={e => {
							const newDate = {
								...date,
								day: Number(e.target.value),
							};

							setDate(newDate);
							emitChange(newDate);
						}}
						onBlur={onBlur}
					>
						<option value=''>{labels?.["SelectDay"]}</option>
						{Array.from({ length: 31 }, (_, i) => (
							<option key={i + 1} value={i + 1}>
								{i + 1}
							</option>
						))}
					</select>
					<SelectArrowIcon />
				</div>
			</div>

			{/* Month */}
			<div>
				{hasError && (
					<span className='error-message key-RequiredField'>
						{!date.month ? labels?.["RequiredField"] : <>&nbsp;</>}
					</span>
				)}
				<div className='club-form-select'>
					<select
						id='month'
						className={`form-field ${
							!date.month && hasError ? "has-error" : ""
						}`}
						value={date.month || ""}
						onChange={e => {
							const newDate = {
								...date,
								month: Number(e.target.value),
							};

							setDate(newDate);
							emitChange(newDate);
						}}
						onBlur={onBlur}
					>
						<option value=''>{labels?.["SelectMonth"]}</option>
						{MONTH_NAMES.map((name, index) => (
							<option key={index} value={index + 1}>
								{name}
							</option>
						))}
					</select>
					<SelectArrowIcon />
				</div>
			</div>

			{/* Year */}
			<div>
				{hasError && (
					<span className='error-message key-RequiredField'>
						{!date.year ? labels?.["RequiredField"] : <>&nbsp;</>}
					</span>
				)}
				<div className='club-form-select'>
					<select
						id='year'
						className={`form-field ${
							!date.year && hasError ? "has-error" : ""
						}`}
						value={date.year || ""}
						onChange={e => {
							const newDate = {
								...date,
								year: Number(e.target.value),
							};

							setDate(newDate);
							emitChange(newDate);
						}}
						onBlur={onBlur}
					>
						<option value=''>{labels?.["SelectYear"]}</option>
						{yearOptions.map(year => (
							<option key={year} value={year}>
								{year}
							</option>
						))}
					</select>
					<SelectArrowIcon />
				</div>
			</div>
		</div>
	);
};
